/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { isObject, get } from "lodash"
import { matchPathname } from "common/utils/uri"
import {
  CHAIN_PATTERN,
  CREATE_CATALOGUE,
  CREATE_MENU,
  INVENTORY,
  OUTLET_ORDERS_PATTERN,
  OUTLET_OVO_TRANSACTIONS_PATTERN,
  OUTLET_TRANSACTIONS_PATTERN,
} from "constants/routes"
import {
  EventAction,
  EventActionEng,
  EventCategory,
  EventCategoryEng,
  EventCategoryPrefix,
  EventLabelKeys,
  UserTypes,
} from "constants/tracking"
import {
  EventAction as OrderManagerEventAction,
  EventCategory as OrderManagerEventCategory,
} from "order-manager/constants/tracking"
import {
  EventAction as EcommerceEventAction,
  EventCategory as EcommerceEventCategory,
} from "ecommerce/constants/tracking"
import {
  isPrd,
  // isStg,
  isDev,
  isStg,
} from "constants/domain"
import {
  getUserGrabPayGrabID,
  getUserType,
  getMerchantGrabID,
  hasEcommerceStore,
  isObserver,
} from "utils/user_helper"
import scribe from "@grab-x/scribe-web-sdk"
import { sessionInfo } from "./session"
import { storageHelper } from "./storage"
import normalizedFacets from "./normalizedFacets"

export interface EventLabelInit {
  selectedLanguage?: string
  clickText?: string
  filterText?: string
  needDefaultLanguage?: boolean
}

export class EventLabel {
  private labelObj: {
    [key in EventLabelKeys]?: string
  } = {}

  constructor(
    {
      needDefaultLanguage = true,
      selectedLanguage,
      clickText,
      filterText,
    } = {} as EventLabelInit,
  ) {
    if (needDefaultLanguage) {
      this.labelObj[EventLabelKeys.defaultLanguageShowed] =
        sessionInfo.langShowed
    }
    if (selectedLanguage) {
      this.labelObj[EventLabelKeys.selectedLanguage] = selectedLanguage
    }
    if (clickText) {
      this.labelObj[EventLabelKeys.clickText] = clickText
    }
    if (filterText) {
      this.labelObj[EventLabelKeys.filterText] = filterText
    }
  }

  public build() {
    return JSON.stringify(this.labelObj)
  }
}

export const getCategory = (): string | null => {
  if (matchPathname(CHAIN_PATTERN)) {
    return EventCategory.storeTab
  }
  if (matchPathname(OUTLET_ORDERS_PATTERN)) {
    return EventCategory.ordersTab
  }
  if (matchPathname(OUTLET_TRANSACTIONS_PATTERN)) {
    return EventCategory.transactionTab
  }
  if (matchPathname(OUTLET_OVO_TRANSACTIONS_PATTERN)) {
    return EventCategory.instoreOVOTransactionTab
  }
  return null
}

export const sendEvent = ({
  action,
  label,
}: {
  action: EventAction
  label: string
}) => {
  if (!window.gtag) {
    return
  }
  const category = getCategory()
  if (!category) {
    return
  }
  gtag("event", action, {
    event_category: category,
    event_label: label,
    analytics_id: storageHelper.getAnalyticsID(),
  })
}

export interface DefaultParameters {
  USER_STATUS: string
  USER_GRAB_ID: string
  PARENT_ENTITY_ID: string
  GRABFOOD_ENTITY_ID: string
  GRABPAY_ID: string
  USER_ROLE: string
  MEX_TYPE: string
  TROY_WHITELIST: string
  LOGIN_TYPE: string
  MERCHANT_GRAB_ID: string
  STORE_GRAB_ID: string
  IS_ECOMMERCE: boolean
}

const isStore = (userType: string) => {
  return (
    userType === UserTypes.store ||
    userType === UserTypes.individualStore ||
    userType === UserTypes.ptnrStore ||
    userType === UserTypes.mlmStore ||
    userType === UserTypes.merchant
  )
}

const isMerchant = (userType: string) => {
  return (
    userType === UserTypes.mlmMerchant ||
    userType === UserTypes.ptnrMerchant ||
    userType === UserTypes.individualMerchant
  )
}

export const getDefaultParameters: () => DefaultParameters = () => {
  const userProfileInfo = storageHelper.getUserProfile()
  const userProfile = storageHelper.getUserProfile()?.user_profile

  let loginType = ""
  let storeGrabId = ""
  const userProfileLinks = userProfile?.links || []
  const userProfileLinkGPData = userProfileLinks.find(
    data => data.link_entity_business_line === "GP",
  )
  const userType = get(userProfileLinkGPData, ["link_entity_type"])

  if (isStore(getUserType(Number(userType)))) {
    loginType = "STORE"
    storeGrabId = get(userProfileLinkGPData, ["link_entity_id"]) ?? ""
  } else if (isMerchant(getUserType(Number(userType)))) {
    loginType = "MERCHANT"
  }

  return {
    USER_STATUS: userProfile?.profile_status ?? "",
    USER_GRAB_ID: userProfile?.grab_id ?? "",
    PARENT_ENTITY_ID: userProfile?.parent_entity_id ?? "",
    GRABFOOD_ENTITY_ID: userProfile?.grab_food_entity_id ?? "",
    GRABPAY_ID: getUserGrabPayGrabID() ?? "",
    USER_ROLE: userProfile?.role ?? "",
    MEX_TYPE:
      // @ts-ignore
      userProfileInfo?.business_line_ids?.join("/") ?? "",
    TROY_WHITELIST: "ACTIVE",
    LOGIN_TYPE: loginType,
    MERCHANT_GRAB_ID: getMerchantGrabID() ?? "",
    STORE_GRAB_ID: storeGrabId ?? "",
    IS_ECOMMERCE: hasEcommerceStore(),
  }
}

export interface ScribeSDK {
  initialize(applicationName: string, isProduction?: boolean): void
  sendEvent(eventName: string, facets?: object, keepalive?: boolean): void
  setAuthToken(token: string): void
  setFacets(facets?: object): void
  getVariable(featureKey: string, facets?: object): any
}
export class WebTracker {
  defaultParameters: DefaultParameters

  scribesdk: ScribeSDK

  isCatalogue: boolean

  isMenuGroup: boolean

  pathname: string

  constructor() {
    this.initializeScribeSDK()
  }

  private initializeScribeSDK() {
    try {
      // @ts-ignore
      this.scribesdk = scribe
      this.scribesdk.initialize("troy-portal", isPrd())
      this.scribesdk.setAuthToken(storageHelper.getToken() ?? "")
    } catch {
      console.log("error occured while setting web sdk tracker")
    }
  }

  private sendEventScribeSDK(
    eventName: string,
    facets?:
      | Record<string, string | number | boolean | null | undefined>
      | undefined,
  ) {
    this.scribesdk.sendEvent(eventName, isObject(facets) ? facets : {})
  }

  // handle event in reused menu component
  private getNormalizedCategory = (
    category:
      | EventCategory
      | EventCategoryEng
      | OrderManagerEventCategory
      | EcommerceEventCategory,
  ) => {
    if (category.startsWith(EventCategoryPrefix.menu)) {
      if (this.pathname?.startsWith(INVENTORY)) {
        // handle inventory events
        return category.replace(
          EventCategoryPrefix.menu,
          EventCategoryPrefix.inventory,
        )
      }
      if (
        (this.pathname?.startsWith(CREATE_MENU) ||
          this.pathname?.startsWith(CREATE_CATALOGUE)) &&
        !category.startsWith(EventCategoryPrefix.menuGroupCreation)
      ) {
        // handle create menu group events
        return category.replace(
          EventCategoryPrefix.menu,
          EventCategoryPrefix.menuGroupCreation,
        )
      }
      if (
        this.isMenuGroup &&
        !category.startsWith(EventCategoryPrefix.menuGroup)
      ) {
        // handle menu group events
        return category.replace(
          EventCategoryPrefix.menu,
          EventCategoryPrefix.menuGroup,
        )
      }
    }
    return category
  }

  setIsCatalogue(isCatalogue: boolean) {
    this.isCatalogue = isCatalogue
  }

  setIsMenuGroup(isMenuGroup: boolean) {
    this.isMenuGroup = isMenuGroup
  }

  setPathname(pathname: string) {
    this.pathname = pathname
  }

  /** Avoid the usage of Record<object> in labels data.  */
  sendEvent({
    category,
    action,
    labels,
  }: {
    category:
      | EventCategory
      | EventCategoryEng
      | OrderManagerEventCategory
      | EcommerceEventCategory
    action:
      | EventAction
      | EventActionEng
      | OrderManagerEventAction
      | EcommerceEventAction
    labels?: object
  }) {
    const normalizedCategory = this.getNormalizedCategory(category)
    const normalizedLabels = labels ? normalizedFacets(labels) : {}
    const observer = isObserver() ? 1 : 0
    if (isDev()) {
      console.log("EVENT", {
        category: normalizedCategory,
        action,
        labels: {
          ...normalizedLabels,
          ...getDefaultParameters(),
          IS_CATALOGUE: this.isCatalogue ? 1 : 0,
          IS_OBSERVER: observer,
        },
      })
    } else {
      // QA needs the tracker event to be logged in staging too
      if (isStg()) {
        console.log("EVENT", {
          category: normalizedCategory,
          action,
          labels: {
            ...normalizedLabels,
            ...getDefaultParameters(),
            IS_CATALOGUE: this.isCatalogue ? 1 : 0,
            IS_OBSERVER: observer,
          },
        })
      }
      const data: any = {
        ...normalizedLabels,
        ...getDefaultParameters(),
        IS_CATALOGUE: this.isCatalogue ? 1 : 0,
        IS_OBSERVER: observer,
      }
      if (normalizedCategory) data.STATENAME = normalizedCategory
      this.sendEventScribeSDK(action, data)
    }
  }
}

export const getResponseErrorEventReason: (resp: any) => string = resp =>
  resp?.error?.response?.data?.reason ?? resp?.error?.toString()

export const getResponseErrorEventLabels: (
  resp: any,
) => { [key: string]: string } = resp => {
  return {
    REASON: getResponseErrorEventReason(resp),
    ERROR_MSG: resp?.error?.response?.data?.message,
    ERROR_TARGET: resp?.error?.response?.data?.target,
  }
}

export const TroyWebTracker = new WebTracker()
